import type { AutobuildRunnerHost } from '@a7y/types';

/**
 * Where a session ran: the self-hosted UKKO fleet, a GitHub-hosted runner, or
 * some other self-hosted fleet.
 *
 * Unlike `TriggerBadge`, this renders for EVERY known value rather than only
 * the notable one. There is no "overwhelming majority" case here — which fleet
 * a session landed on is the question being asked, so suppressing the common
 * answer would leave the reader unable to tell "GitHub-hosted" from "recorded
 * before this marker existed".
 *
 * Null still renders nothing. Sessions predating the `runner` marker carry no
 * provenance, and claiming a fleet we did not record is the failure the
 * null-is-not-scheduled rule exists to prevent.
 */
const STYLES: Record<AutobuildRunnerHost, { label: string; className: string; title: string }> = {
  ukko: {
    label: 'UKKO',
    className: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
    title: 'Ran on the self-hosted UKKO fleet',
  },
  github: {
    label: 'GitHub',
    className: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
    title: 'Ran on a GitHub-hosted runner',
  },
  'self-hosted': {
    label: 'Self-hosted',
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
    title: 'Ran on a self-hosted runner that is not the UKKO fleet',
  },
};

export function RunnerBadge({
  runnerHost,
  runnerName,
}: {
  runnerHost: AutobuildRunnerHost | null;
  runnerName?: string | null;
}) {
  if (runnerHost == null) return null;
  const style = STYLES[runnerHost];
  return (
    <span
      className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap ${style.className}`}
      title={runnerName ? `${style.title} — ${runnerName}` : style.title}
    >
      {style.label}
    </span>
  );
}
